import { useState } from 'react'

import { joiResolver } from '@hookform/resolvers/joi'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import Joi from 'joi'
import { SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  ControlledCheckbox,
  ControlledSelector,
  ControlledTextField,
} from 'components/common/controller-wrapper'
import { EntityConstants } from 'constants/index'
import {
  useCreateAppVersionMutation,
  useDeleteAppVersionMutation,
  useGetAppVersionsQuery,
  useUpdateAppVersionMutation,
} from 'generated/graphql'
import { GET_APP_VERSIONS } from 'graphql/queries'

const PLATFORM_OPTIONS = ['All', 'iOS', 'Android']
const APP_IDENTIFIER_OPTIONS = ['DriverApp']

interface AppVersionFormValues {
  appIdentifier: string
  version: string
  minimumVersion: string
  forceUpdate: boolean
  updateMessage: string
  platform: string
}

const DEFAULT_VALUES: AppVersionFormValues = {
  appIdentifier: 'DriverApp',
  version: '',
  minimumVersion: '',
  forceUpdate: false,
  updateMessage: '',
  platform: 'All',
}
const validationSchema = () =>
  Joi.object({
    appIdentifier: Joi.string().required().max(50).label('App Identifier'),
    version: Joi.string().required().max(20).label('Version'),
    minimumVersion: Joi.string().required().max(20).label('Minimum Version'),
    updateMessage: Joi.string()
      .max(EntityConstants.MAX_NOTE_LENGTH)
      .optional()
      .allow(null, '')
      .label('Update Message'),
    platform: Joi.string().max(20).label('Platform'),
    forceUpdate: Joi.bool().label('Force update'),
  })

export const Mobile = () => {
  const [openDialog, setOpenDialog] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const { data, loading: loadingVersions } = useGetAppVersionsQuery()

  const [createAppVersion, { loading: creating }] = useCreateAppVersionMutation({
    refetchQueries: [{ query: GET_APP_VERSIONS }],
  })

  const [updateAppVersion, { loading: updating }] = useUpdateAppVersionMutation({
    refetchQueries: [{ query: GET_APP_VERSIONS }],
  })

  const [deleteAppVersion, { loading: deleting }] = useDeleteAppVersionMutation({
    refetchQueries: [{ query: GET_APP_VERSIONS }],
  })

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<AppVersionFormValues>({
    defaultValues: DEFAULT_VALUES,
    resolver: joiResolver(validationSchema()),
  })
  const updateMessage = watch('updateMessage')
  const handleOpenCreate = () => {
    setEditingId(null)
    reset(DEFAULT_VALUES)
    setOpenDialog(true)
  }

  const handleOpenEdit = (version: any) => {
    setEditingId(version.id)
    reset({
      appIdentifier: version.appIdentifier,
      version: version.version,
      minimumVersion: version.minimumVersion,
      forceUpdate: version.forceUpdate,
      updateMessage: version.updateMessage ?? '',
      platform: version.platform,
    })
    setOpenDialog(true)
  }

  const handleClose = () => {
    setOpenDialog(false)
  }

  const handleDelete = (id: string) => {
    deleteAppVersion({ variables: { id: Number(id) } }).then(() => {
      toast.success('App version deleted successfully')
    })
  }

  const onSubmit: SubmitHandler<AppVersionFormValues> = (values) => {
    if (editingId) {
      updateAppVersion({
        variables: {
          input: { id: editingId, ...values },
        },
      }).then(() => {
        toast.success('App version updated successfully')
        handleClose()
      })
    } else {
      createAppVersion({
        variables: {
          input: values,
        },
      }).then(() => {
        toast.success('App version created successfully')
        handleClose()
      })
    }
  }

  const saving = creating || updating
  const appVersions = data?.appVersions ?? []

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant='h5'>Mobile App Versions</Typography>
        <Button
          variant='contained'
          color='primary'
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}>
          Create New Version
        </Button>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant='h6' sx={{ mb: 2 }}>
          Published Versions
        </Typography>

        {loadingVersions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>App</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Min. Version</TableCell>
                  <TableCell>Force Update</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell align='right'>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {appVersions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align='center'>
                      <Typography color='text.secondary'>No app versions found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  appVersions.map((v: any) => (
                    <TableRow key={v.id}>
                      <TableCell>{v.appIdentifier}</TableCell>
                      <TableCell>{v.platform}</TableCell>
                      <TableCell>{v.version}</TableCell>
                      <TableCell>{v.minimumVersion}</TableCell>
                      <TableCell>{v.forceUpdate ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{v.updateMessage ?? '—'}</TableCell>
                      <TableCell align='right'>
                        <Tooltip title='Edit'>
                          <IconButton size='small' onClick={() => handleOpenEdit(v)}>
                            <EditIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Delete'>
                          <IconButton
                            size='small'
                            disabled={deleting}
                            onClick={() => handleDelete(v.id)}>
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={openDialog} onClose={handleClose} maxWidth='sm' fullWidth>
        <DialogTitle>{editingId ? 'Edit App Version' : 'Create App Version'}</DialogTitle>
        <DialogContent>
          <Box component='form' sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <ControlledSelector
                  name='appIdentifier'
                  control={control}
                  label='App Identifier'
                  required
                  error={!!errors.appIdentifier}
                  helperText={errors.appIdentifier?.message}
                  options={APP_IDENTIFIER_OPTIONS.map((opt) => ({ value: opt, label: opt }))}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ControlledSelector
                  name='platform'
                  control={control}
                  label='Platform'
                  required
                  error={!!errors.platform}
                  helperText={errors.platform?.message}
                  options={PLATFORM_OPTIONS.map((opt) => ({ value: opt, label: opt }))}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ControlledTextField
                  name='version'
                  control={control}
                  label='Version'
                  required
                  error={!!errors.version}
                  helperText={errors.version?.message}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ControlledTextField
                  name='minimumVersion'
                  control={control}
                  label='Minimum Version'
                  required
                  error={!!errors.minimumVersion}
                  helperText={errors.minimumVersion?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <ControlledTextField
                  name='updateMessage'
                  control={control}
                  label='Update Message'
                  multiline
                  rows={3}
                  error={!!errors.updateMessage}
                  helperText={errors.updateMessage?.message}
                  endAdornment={<InputAdornment position='end'>{updateMessage.length}/{EntityConstants.MAX_NOTE_LENGTH}</InputAdornment>}
                />
              </Grid>

              <Grid item xs={12}>
                <ControlledCheckbox name='forceUpdate' control={control} label='Force Update' />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton
            variant='contained'
            loading={saving}
            type='submit'
            onClick={handleSubmit(onSubmit)}>
            {editingId ? 'Save Changes' : 'Create'}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
